import React, { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, MapPin, Phone, Car, RefreshCw, Check, X, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  requested: { label: "Solicitada", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  confirmed: { label: "Confirmada", color: "bg-green-500/20 text-green-300 border-green-500/30" },
  attended: { label: "Asistió", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  no_show: { label: "No asistió", color: "bg-red-500/20 text-red-300 border-red-500/30" },
  rescheduled: { label: "Reagendada", color: "bg-orange-500/20 text-orange-300 border-orange-500/30" },
  cancelled: { label: "Cancelada", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
};

const AGENCIES = {
  nissan: "Eje 1 Nte. 70, Guerrero, Cuauhtémoc, CDMX",
  chevrolet: "Calz. Zaragoza 1198, Agrícola Pantitlán, Iztacalco, CDMX",
  kia: "Calzada Taxqueña 1521, Coyoacán, CDMX",
  jac: "Calzada Taxqueña 1521, Coyoacán, CDMX",
  volkswagen: "Av. Revolución 1220, Guadalupe Inn, Álvaro Obregón, CDMX",
};

export default function AppointmentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const q = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await axios.get(`${API}/api/admin/appointments${q}`);
      if (data?.success) setItems(data.appointments || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/admin/appointments/${id}`, { status });
      load();
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [filter]);

  const fmtDT = (s) => {
    if (!s) return "-";
    try {
      const d = new Date(s);
      return d.toLocaleString("es-MX", { weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return s; }
  };

  const groupedByDay = items.reduce((acc, v) => {
    const day = (v.scheduled_at || "").slice(0, 10) || "sin-fecha";
    if (!acc[day]) acc[day] = [];
    acc[day].push(v);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="appointments-tab">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calendar className="w-6 h-6 text-orange-400" />
            Citas & Visitas
          </h2>
          <p className="text-slate-400 text-sm mt-1">Visitas agendadas por el chatbot y por los asesores. Confirma o marca asistencia aquí.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["all", ...Object.keys(STATUS_META)].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`appt-filter-${s}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === s ? "bg-orange-500 text-white" : "bg-slate-800 text-slate-400 hover:text-white"
              }`}
            >
              {s === "all" ? "Todas" : STATUS_META[s]?.label || s}
            </button>
          ))}
          <button onClick={load} className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white" data-testid="appt-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {items.length === 0 && !loading && (
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-8 text-center text-slate-500">
          <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Aún no hay citas agendadas.</p>
        </div>
      )}

      {Object.entries(groupedByDay).sort((a, b) => b[0].localeCompare(a[0])).map(([day, list]) => (
        <div key={day} className="space-y-2">
          <div className="text-slate-400 text-xs uppercase tracking-widest font-medium mt-4">
            {day === "sin-fecha" ? "Sin fecha" : new Date(day).toLocaleDateString("es-MX", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
          {list.map((v) => {
            const meta = STATUS_META[v.status] || STATUS_META.requested;
            const agencyKey = String(v.brand || "").toLowerCase();
            const agency = AGENCIES[agencyKey];
            return (
              <div key={v.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 hover:border-orange-500/40 transition-colors" data-testid={`appt-item-${v.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                      <span className="text-white font-medium text-sm">{fmtDT(v.scheduled_at)}</span>
                      <span className={`ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                      {v.brand && <span className="flex items-center gap-1"><Car className="w-3 h-3" />{v.brand}{v.vehicle_of_interest ? ` — ${v.vehicle_of_interest}` : ""}</span>}
                      {v.client_name && <span>{v.client_name}</span>}
                      {v.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{v.client_phone}</span>}
                    </div>
                    {agency && (
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
                        <MapPin className="w-3 h-3" />{agency}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setStatus(v.id, "attended")}
                      className="px-2.5 py-1 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs hover:bg-emerald-500/30 flex items-center gap-1"
                      data-testid={`appt-mark-attended-${v.id}`}
                    >
                      <Check className="w-3 h-3" />Asistió
                    </button>
                    <button
                      onClick={() => setStatus(v.id, "no_show")}
                      className="px-2.5 py-1 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-xs hover:bg-red-500/30 flex items-center gap-1"
                      data-testid={`appt-mark-noshow-${v.id}`}
                    >
                      <X className="w-3 h-3" />No asistió
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
